// AKBSに関するUTILS
// このファイルでthisは使用禁止です。（本番でundefineになります！！）


import * as constantsAKBS from '@libs/constantsAKBS'
import * as dialogs from '@libs/dialogs'
import * as utils from '@libs/utils'
import BigNumber from "bignumber.js"

/**
 * 住所（または都道府県）から営業所（東京／千葉）を決める
 * 住所の先頭が 東京都・神奈川県 なら東京、埼玉県・千葉県 なら千葉。それ以外・空なら空文字（画面で選ばせる）
 * @param {String} address 住所または都道府県
 * @returns {String} 営業所。決まらなければ空文字
 */
export function salesOfficeFromAddress(address) {
  const text = String(address || '').trim()
  for (const pref of Object.keys(constantsAKBS.SALES_OFFICE_BY_PREF)) {
    if (text.startsWith(pref)) {
      return constantsAKBS.SALES_OFFICE_BY_PREF[pref]
    }
  }
  return ''
}

/**
 * 住所からエリア名（AREA_SELECTIONS のどれか）を決める。雇用指示の「雇用指示営業所」「担当営業所」の初期値に使う
 * ・神奈川県・千葉県・埼玉県 → その県のエリア
 * ・東京都 → 都道府県のあとが 23 区の名前で始まればその区のエリア。それ以外（多摩）は「多摩エリア」（北・南は住所では決まらないので画面で選び直す）
 * ・それ以外・空 → 空（画面で選ぶ）
 * @param {String} address 住所（都道府県から始まるもの）
 * @returns {String} エリア名。決まらなければ空文字
 */
export function areaFromAddress(address) {
  const text = String(address || '').trim()
  if (text.startsWith('神奈川')) {
    return '神奈川エリア'
  }
  if (text.startsWith('千葉')) {
    return '千葉エリア'
  }
  if (text.startsWith('埼玉')) {
    return '埼玉エリア'
  }
  if (text.startsWith('東京')) {
    const rest = text.replace(/^東京都?/, '')
    const ward = constantsAKBS.AREA_SELECTIONS
      .map(a => a.replace('エリア', ''))
      .find(n => n.endsWith('区') && rest.startsWith(n))
    return ward ? ward + 'エリア' : '多摩エリア'
  }
  return ''
}


/**
 * 勤務形態による1ヶ月の時間を出すための係数取得
 * @param {*} val 勤務形態区分
 * @returns
 */
export function attendanceCoef(val) {
  // 1か月の対象曜日の平均出現回数
  let res = 4.3
  // 毎週
  if (!val || val == '00') {
    return res
  }

  switch (val) {
    // 第5週（第12345週を選んだ時に、毎週と同じにするために係数を0.34ではなく0.3にする）
    case '05':
      res = 0.3
      break;

    // 隔週
    case '10':
      res = 2.15
      break;

    // 上記以外
    default:
      res = 1
      break;
  }
  return res
}

/**
 * 1か月の実働時間取得（対象曜日のみ）
 * @param {*} workingHours 対象曜日の実働時間
 * @param {*} attendances 対象曜日の勤務形態配列
 * @returns
 */
export function getHoursByMonth(workingHours, attendances) {
  let total = 0

  // 勤務曜日該当
  if (workingHours && attendances && attendances.length) {
    attendances.forEach(at => {

      const hours = BigNumber(attendanceCoef(at.id)).times(workingHours)

      total = BigNumber(total).plus(hours)
    })

    return Number(total)
  } else {
    return 0
  }
}

/**
 * DCの1週間の実働時間
 * @param {*} list 勤務詳細配列
 * @param {*} typeChanged 契約変更の場合
 * @returns
 */
export function getHoursForOneWeek(list, typeChanged) {
  let total = 0

  if (typeChanged) {
    list.forEach(aw => {
      constantsAKBS.WEEK_LIST.forEach(w => {
        total = BigNumber(getHoursByMonth(aw['workingHours' + w.id + 'Changed'], aw['attendance' + w.id + 'Changed'])).plus(total)
      })
    })
  } else {
    list.forEach(aw => {
      constantsAKBS.WEEK_LIST.forEach(w => {
        total = BigNumber(getHoursByMonth(aw['workingHours' + w.id], aw['attendance' + w.id])).plus(total)
      })
    })
  }

  total = BigNumber(total).div(4.3)

  // 少数第3位まで表示（それ以下は切り捨て）
  return Number(BigNumber(total).decimalPlaces(3, BigNumber.ROUND_DOWN))
}

/**
 * 1つの契約につき、月額を求める
 * @param {*} row 1件の勤務（雇用指示1行）
 * @param {*} wage 対象の賃金（省略時は一番最後の賃金＝最新の賃金。賃金は適用日順に並んでいる）
 * @returns
 */
export function getMonthlyWage(row, wage) {
  let hours = 0
  let salary = 0

  // 作業終了が入っている場合は0円
  if (row.endDateWork) {
    return 0
  }

  // 1か月の実働時間
  constantsAKBS.WEEK_LIST.forEach(w => {
    hours = BigNumber(getHoursByMonth(row['workingHours' + w.id], row['attendance' + w.id])).plus(hours)
  })

  // 賃金の指定がなければ、一番最後の賃金（最新の賃金）で計算する。賃金に終了日は無く、適用日順に並んでいる
  const wages = row.wages || []
  const target = wage || wages[wages.length - 1]
  if (target && Number(target.basicWage)) {
    salary = BigNumber(hours).times(Number(target.basicWage))
  }

  // 円未満は繰り上げる
  return Number(BigNumber(salary).decimalPlaces(0, BigNumber.ROUND_UP))
}

/**
 * 1か月の出勤回数
 * 勤務形態の係数（毎週4.3・隔週2.15・第n週1・第5週0.3）をそのまま足す
 * @param {*} row 1件の勤務（雇用指示1行）
 * @returns 1か月の出勤回数
 */
export function getWorkDaysByMonth(row) {
  let days = 0

  constantsAKBS.WEEK_LIST.forEach(w => {
    const attendances = row['attendance' + w.id]
    if (!attendances || !attendances.length) {
      return
    }
    attendances.forEach(at => {
      days = BigNumber(days).plus(attendanceCoef(at.id))
    })
  })

  return Number(days)
}

/**
 * 1か月の交通費
 * 1ヶ月支給はそのまま、1日支給は出勤回数をかけて月額に直す
 * @param {*} fares [{ type: 支給区分（0:無 1:1日 2:1ヶ月）, fee: 金額 }]
 * @param {*} workDays 1か月の出勤回数
 * @returns 1か月の交通費
 */
export function getMonthlyFare(fares, workDays) {
  let total = BigNumber(0)

  fares.forEach(f => {
    const fee = Number(f.fee) || 0

    if (Number(f.type) === 1) {
      total = total.plus(BigNumber(fee).times(workDays))
    } else if (Number(f.type) === 2) {
      total = total.plus(fee)
    }
  })

  // 円未満は繰り上げる
  return Number(total.decimalPlaces(0, BigNumber.ROUND_UP))
}

/**
 * 交通費の時給換算
 * 1日支給は「1週間の交通費 ÷ 1週間の実働時間」（係数は分子・分母で打ち消し合う）
 * 1ヶ月支給は「月額 ÷ 1か月の実働時間」
 * @param {*} row 1件の勤務（雇用指示1行）
 * @param {*} fares [{ type: 支給区分（0:無 1:1日 2:1ヶ月）, fee: 金額 }]
 * @returns 1時間あたりの交通費
 */
export function getFarePerHour(row, fares) {
  // 1週間の勤務日数・実働時間（1日支給用）
  let workCount = 0
  let workHours = 0
  // 1か月の実働時間（1ヶ月支給用）
  let monthlyHours = 0

  constantsAKBS.WEEK_LIST.forEach(w => {
    const attendances = row['attendance' + w.id]
    if (attendances && attendances.length) {
      workCount++
      workHours = BigNumber(workHours).plus(Number(row['workingHours' + w.id]) || 0)
    }
    monthlyHours = BigNumber(getHoursByMonth(row['workingHours' + w.id], attendances)).plus(monthlyHours)
  })

  let daily = BigNumber(0)
  let monthly = BigNumber(0)
  fares.forEach(f => {
    const fee = Number(f.fee) || 0
    if (Number(f.type) === 1) {
      daily = daily.plus(fee)
    } else if (Number(f.type) === 2) {
      monthly = monthly.plus(fee)
    }
  })

  let total = BigNumber(0)

  // 勤務が入っていないうちは割れないので、その分は加算しない
  if (Number(workHours)) {
    total = total.plus(daily.times(workCount).div(workHours))
  }
  if (Number(monthlyHours)) {
    total = total.plus(monthly.div(monthlyHours))
  }

  // 少数以下繰り上げ
  return Number(total.decimalPlaces(0, BigNumber.ROUND_UP))
}

/**
 * 小数を含む引き算
 * @param {*} val1 引かれる値
 * @param {*} val2 引く値
 * @returns 差
 */
export function getDiffFew(val1, val2) {
  const res = BigNumber(val1).minus(val2)
  return Number(res)
}

/**
 * 交通費時給換算+時給
 * @param {*} row 対象行
 * @param {*} type 種別
 * @returns 交通費時給分
 */
export function getHourlyWage(row, type) {
  let totalWage = 0
  let totalFare = 0
  let c = ''
  if (type == '契約変更') c = 'Changed'

  // for (let i = 0; i < list.length; i++) {
  //   const row = list[i]

    // 契約変更でチェックがついていない場合はスキップ
    if (type == '契約変更' && !row.isChecked) return

    // 交通費時給に換算するため
    // 勤務回数カウント
    let workCount = 0
    // 実働時間合計（単純加算）
    let workHours = 0
    // 1か月分の時間を求める
    let total = 0

    constantsAKBS.WEEK_LIST.forEach(w => {
      const key = w.id + c

      // 勤務形態
      const att = row['attendance' + key]

      // 実働時間
      const hours = Number(row['workingHours' + key])

      // 勤務あり
      if (att && att.length) {
        // 勤務日あり
        workCount++
        workHours += hours
      }
      total = BigNumber(getHoursByMonth(row['workingHours' + w.id], row['attendance' + w.id])).plus(total)
    })

    // 1日分の交通費を1時間分にする（交通費もそのままなので、勤務回数や時間も係数なしのそのまま）
    let fare = 0
    if (row['kubunTrain' + c] == 1) {
      fare += Number(row['fareTrain' + c])
    }
    if (row['kubunBus' + c] == 1) {
      fare += Number(row['fareBus' + c])
    }
    if (row['kubunBicycleParking' + c] == 1) {
      fare += Number(row['fareBicycleParking' + c])
    }
    // 交通費合計×勤務日数＝合計交通費
    fare = fare * workCount
    // 合計交通費÷時間
    fare = Number(BigNumber(fare).div(workHours))

    // 定期の交通費を1時間分にする
    let fare2 = 0
    if (row['kubunTrain' + c] == 2) {
      fare2 += Number(row['fareTrain' + c])
    }
    if (row['kubunBus' + c] == 2) {
      fare2 += Number(row['fareBus' + c])
    }
    if (row['kubunBicycleParking' + c] == 2) {
      fare2 += Number(row['fareBicycleParking' + c])
    }

    fare2 = Number(BigNumber(fare2).div(total))

    // 少数以下繰り上げ
    totalFare = Math.ceil(Number(BigNumber(fare).plus(fare2)))

    totalWage = Number(row['basicWage' + c]) + totalFare
  // }

  return totalWage
}


/**
 * エラー文の後ろに、どう直すかの一言を足す（ロック中の行で引っかかったときなどに使う）
 * @param msg エラー文
 * @param hint 一言（無ければそのまま）
 */
function withHint(msg, hint) {
  return hint ? msg + '\n\n' + hint : msg
}

/**
 * 勤務形態のチェック
 * @param att 勤務形態の配列
 * @param week 曜日名
 * @param hint エラー文の後ろに足す一言（省略可）
 */
export async function checkAttendance(att, week, hint) {
  if (!att || !att.length) {
    return true
  }
  const attId = att.map((a) => a.id)

  let msg = '毎週'
  if ((attId.includes('00') || attId.includes('10')) && attId.length > 1) {
    if (attId.includes('10')) {
      msg = '隔週'
    }
    await dialogs.showErrorDialog('勤務形態が不正', withHint(week + '曜日の勤務形態区分をご確認ください。\r\n「' + msg + '」を選択している場合、他の形態は選択できません。', hint))
    return false
  }
  if (attId.length >= 5) {
    await dialogs.showErrorDialog('勤務形態', withHint(week + '曜日の勤務形態区分をご確認ください。\r\n第1～5週全て勤務の場合「毎週」を選択してください。', hint))
    return false
  }
  return true
}

/**
 * 時間未入力
 * @param {*} item 値
 * @param {*} name 名前
 * @param hint エラー文の後ろに足す一言（省略可）
 * @returns
 */
export async function checkNoTimes(item, name, hint) {
  const err = withHint('勤務がない日は、曜日をクリックして勤務をOFFにしてください。\r\n※曜日をクリックするとOFFにできます。', hint)
  if (!item) {
    await dialogs.showErrorDialog(name + 'が未入力です', err)
    return false
  }
  const time = item.split(':')
  if (!time[0] || !time[1]) {
    await dialogs.showErrorDialog(name + 'が未入力です', err)
    return false
  }
  return true
}

/**
 * 勤務時間確認
 * @param time 勤務時間
 * @param week 曜日名
 * @param hint エラー文の後ろに足す一言（省略可）
 */
export async function checkTimes(time, week, hint) {
  const arr = time.split(':')
  if (!arr) {
    await dialogs.showErrorDialog(week + '曜日の勤務時間が不正', withHint('勤務時間をご確認ください。不正です。', hint))
    return false
  }
  if (!arr[0]) {
    await dialogs.showErrorDialog(week + '曜日の勤務時間が不正', withHint('勤務時間の「時間」をご確認ください。不正です。', hint))
    return false
  }
  if (!arr[1]) {
    await dialogs.showErrorDialog(week + '曜日の勤務時間が不正', withHint('勤務時間の「分」をご確認ください。不正です。', hint))
    return false
  }
  return true
}

/**
 * 勤務時間確認
 * @param start 開始
 * @param end 終了
 * @param week 曜日名
 * @param hint エラー文の後ろに足す一言（省略可）
 */
export async function checkTimes2(start, end, week, hint) {
  const s = Number(start.replace(':', ''))
  const e = Number(end.replace(':', ''))

  if (s >= e) {
    await dialogs.showErrorDialog(week + '曜日の勤務時間が不正', withHint('開始・終了 時間をご確認ください。', hint))
    return false
  }

  return true
}

/**
 * 休憩時間確認
 * @param start 開始時間
 * @param end 終了時間
 * @param workingHours 実働時間
 * @param week 曜日名
 * @param warnings 拘束時間が6時間以下で休憩があるときの確認をまとめる入れ物（配列）。渡すと確認ダイアログを出さずに { week, hours, breakHours } を足して true を返す
 * @param hint エラー文の後ろに足す一言（省略可）
 */
export async function  checkBreakTime(start, end, workingHours, week, warnings, hint) {
  if (Number(workingHours) <= 0) {
    await dialogs.showErrorDialog(week + '曜日の実働時間', withHint('実働時間を入力してください。不正な値です。', hint))
    return false
  }
  // 開始・狩猟時間から拘束時間を算出
  const hours = utils.getHours(start, end)
  // 拘束時間と入力された実働時間を比較した差分
  const bh = getDiffFew(hours, workingHours)

  if (bh < 0) {
    await dialogs.showErrorDialog(week + '曜日の実働時間', withHint('矛盾しています。拘束時間よりも実働時間が長いです。', hint))
    return false
  }

  if (hours <= 6) {
    if (bh > 0) {
      if (Array.isArray(warnings)) {
        warnings.push({ week, hours, breakHours: bh })
      } else if (await dialogs.showWarningConfirmDialog(week + '曜日の実働時間', '拘束時間は' + hours + '時間ですが休憩時間が発生しています。お間違いないですか？') != 'YES') {
        return false
      }
    }
  } else if (hours > 6 && hours <= 8) {
    if (bh < 0.75) {
      await dialogs.showErrorDialog(week + '曜日の実働時間', withHint('拘束時間が6時間を超え、8時間以下の場合は最低でも45分以上休憩を入れてください。', hint))
      return false
    }
  } else if (hours > 8) {
    if (bh < 1) {
      await dialogs.showErrorDialog(week + '曜日の実働時間', withHint('拘束時間が8時間を超える場合は1時間以上休憩を入れてください。', hint))
      return false
    }
  }

  return true
}

/**
 * 交通費矛盾確認
 * @param {*} key 対象プロパティ 
 * @param {*} name 対象の名前
 * @param {*} row 対象行
 * @returns 
 */
export async function checkTrans(key, name, row, msg, teiki, type) {
  let c = ''
  if (type == '契約変更') {
    c = 'Changed'
  }

  if (['kubunTrain' + c, 'kubunBus' + c, 'kubunBicycleParking' + c].includes(key)) {
    const key2 = key.replace('kubun', 'fare')
    if (row[key] === 0 && Number(row[key2]) !== 0) {
      await dialogs.showErrorDialog(msg + name + 'が不正', '区分は「無し」ですが、交通費が0円ではありません。ご確認ください。')
      return false
    } else if (row[key] > 0 && Number(row[key2]) === 0) {
      await dialogs.showErrorDialog(msg + name + 'が不正', '交通費が0円で、区分と矛盾しています。ご確認ください。')
      return false
    }

    // 定期の場合
    if (row[key] == 2 && Number(row[key2]) > 0) {
      if (!teiki[key]) {
        teiki[key] = []
      }
      teiki[key].push((msg || '1つ目の') + name.replace(' 区分', ' (1ヶ月)') + '：' + row[key2] + '円')
    }
  }
  return true
}

/**
 * 勤務形態を画面表示用の選択肢（{ id, name }）に直す
 * DBからは ['00'] のようなコードの配列で返るため、そのままでは Multiselect で表示できない
 * @param {Array} val 勤務形態のコードの配列
 * @returns 勤務形態の選択肢の配列
 */
export function toAttendance(val) {
  if (!Array.isArray(val)) {
    return []
  }

  return val
    .map(code => constantsAKBS.ATTENDANCE.find(a => a.id == code))
    .filter(a => a)
    // 定数をそのまま持たせると画面側の書き換えが定数に及ぶため複製して返す
    .map(a => ({ ...a }))
}

/**
 * 雇用指示の勤務形態を画面表示用の選択肢に直す（曜日分まとめて）
 * @param {Array} list 雇用指示の配列
 */
export function setAttendance(list) {
  for (const row of list || []) {
    for (const w of constantsAKBS.WEEK_LIST) {
      row['attendance' + w.id] = toAttendance(row['attendance' + w.id])
    }
  }
}

// -------------------------------------------------------------------------------------------------
// 登録済み一覧の表示用（雇用指示・契約詳細の行を閲覧で出すときの文字。カードと一覧表で共用）
// -------------------------------------------------------------------------------------------------

/**
 * その曜日は勤務なしか（勤務形態が無く、実働も時間も無い）
 * @param {Object} row 雇用指示・契約詳細の行（DBから来たままの行。勤務形態はコードの配列）
 * @param {Object} w 曜日（WEEK_LIST の1つ）
 */
export function isDayOff(row, w) {
  const at = row['attendance' + w.id]
  const hours = Number(row['workingHours' + w.id]) || 0
  return (!Array.isArray(at) || !at.length) && !hours && !row['start' + w.id] && !row['end' + w.id]
}

/**
 * 勤務形態のコード配列 → 名前の配列（毎週・第1・隔週…。知らないコードはそのまま）
 * @param {Array} codes コード
 */
export function attendanceNames(codes) {
  if (!Array.isArray(codes) || !codes.length) {
    return []
  }
  return codes.map(c => {
    const a = constantsAKBS.ATTENDANCE.find(x => x.id == c)
    return a ? a.name : c
  })
}

/**
 * 実働時間の表示（7h。無ければ -）
 * @param {*} hours 実働時間
 */
export function hoursText(hours) {
  return hours === null || hours === undefined || hours === '' ? '-' : `${Number(hours)}h`
}

/**
 * 祝日区分の名前（FULL・1時間・2時間・3時間・無。無ければ -）
 * @param {*} code 祝日区分
 */
export function holidayName(code) {
  if (code === null || code === undefined || code === '') {
    return '-'
  }
  const h = constantsAKBS.HOLIDAYTYPE_SELECTIONS.find(x => Number(x.id) === Number(code))
  if (h) {
    return h.name
  }
  // 選択肢に無い時間（1.5 など。0.25 刻みで入れられる）は「○時間」で出す
  return isNaN(code) ? String(code) : `${Number(code)}時間`
}

/**
 * 有・無（1 → 有、0 → 無、無ければ -）
 * @param {*} val 値
 */
export function yesNo(val) {
  if (val === null || val === undefined || val === '') {
    return '-'
  }
  return Number(val) === 1 ? '有' : '無'
}

// このファイルでthisは使用禁止です。（本番でundefineになります！！）